/*
 * Copyright © 2024-present Mia s.r.l.
 * All rights reserved
 */

import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import config from '../config';
import './HelmPreviewPanel.css';

/**
 * HelmPreviewPanel Component
 *
 * Connects to helm-chart-container-live via WebSocket and shows:
 * - Live status (linting → dry-run → success/error)
 * - Lint issues with actionable suggestions
 * - Dry-run issues
 * - Rendered YAML templates output
 * - A button to re-run the validation
 */
const HelmPreviewPanel = ({
  sessionId,
  projectId,
  serviceName,
  enabled = false,
  containerPort = 3022,
  onRunTest,
}) => {
  const [status, setStatus] = useState('idle');
  const [lintResult, setLintResult] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('issues'); // 'issues' | 'templates' | 'logs'
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const logsEndRef = useRef(null);
  const templatesRef = useRef(null);
  const isDoneRef = useRef(false); // true when validation reached a terminal state
  const [copied, setCopied] = useState(false);

  const allIssues = [
    ...(lintResult?.issues || []).map(i => ({...i, source: 'lint'})),
    ...(dryRunResult?.issues || []).map(i => ({...i, source: 'dry-run'})),
  ];
  const errorCount = allIssues.filter(i => i.severity === 'error').length;
  const warnCount = allIssues.filter(i => i.severity === 'warning').length;

  const handleCopyTemplates = useCallback(() => {
    const text = dryRunResult?.renderedTemplates || '';
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [dryRunResult]);

  const handleScrollTemplatesBottom = useCallback(() => {
    if (templatesRef.current) {
      templatesRef.current.scrollTop = templatesRef.current.scrollHeight;
    }
  }, []);

  const [logCopied, setLogCopied] = useState(false);

  const handleCopyLogs = useCallback(() => {
    const text = logs.map(e => `${e.ts}  ${e.msg}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setLogCopied(true);
      setTimeout(() => setLogCopied(false), 2000);
    }).catch(() => {});
  }, [logs]);

  const handleScrollLogsBottom = useCallback(() => {
    logsEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, []);

  const handleDownloadTemplates = useCallback(() => {
    const text = dryRunResult?.renderedTemplates || '';
    if (!text) return;
    const blob = new Blob([text], {type: 'text/plain'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${serviceName || 'helm-templates'}.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  }, [dryRunResult, serviceName]);

  const appendLog = useCallback((msg) => {
    setLogs(prev => {
      const next = [...prev, {msg, ts: new Date().toLocaleTimeString()}];
      return next.length > 500 ? next.slice(-500) : next;
    });
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [logs]);

  const handleWsMessage = useCallback((raw) => {
    let msg;
    try {
      msg = JSON.parse(raw);
    } catch (_) {
      return;
    }

    switch (msg.type) {
      case 'state': {
        const d = msg.data;
        const newStatus = d.status || 'idle';
        setStatus(newStatus);
        if (newStatus === 'success' || newStatus === 'error') {
          isDoneRef.current = true;
        }
        if (d.lintResult) setLintResult(d.lintResult);
        if (d.dryRunResult) setDryRunResult(d.dryRunResult);
        break;
      }
      case 'log':
        appendLog(msg.message);
        break;
      case 'lint-complete':
        if (msg.issues) setLintResult(prev => ({...(prev || {}), issues: msg.issues, success: msg.success, output: msg.output || ''}));
        break;
      case 'dry-run-complete':
        if (msg.issues) setDryRunResult(prev => ({...(prev || {}), issues: msg.issues, success: msg.success, renderedTemplates: msg.renderedTemplates || ''}));
        break;
      case 'error':
        appendLog('❌ ' + msg.message);
        break;
      default:
        break;
    }
  }, [appendLog]);

  const connectWs = useCallback(() => {
    if (!sessionId || !enabled) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return; // CONNECTING or OPEN
    isDoneRef.current = false; // reset on each new connection attempt

    const isProduction = typeof window !== 'undefined' &&
                         !window.location.origin.includes('localhost') &&
                         !window.location.origin.includes('127.0.0.1');

    let wsUrl;
    if (isProduction) {
      const publicBaseUrl = (config.publicBaseUrl || '').replace(/\/$/, '');
      const wsBase = publicBaseUrl.replace(/^http/, 'ws');
      wsUrl = `${wsBase}/api/preview/helm-chart-container-live/ws?sessionId=${encodeURIComponent(sessionId)}`;
    } else {
      // Route through Vite proxy (/helm-chart-container-live/ws → localhost:3022/ws)
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${proto}//${window.location.host}/helm-chart-container-live/ws?sessionId=${encodeURIComponent(sessionId)}`;
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      appendLog('🔌 Connected to Helm live status');
    };

    ws.onmessage = (e) => handleWsMessage(e.data);

    ws.onclose = (evt) => {
      setIsConnected(false);
      // Don't reconnect if validation is done or server explicitly rejected the session
      if (isDoneRef.current || evt.code === 4000 || evt.code === 4004) {
        if (evt.code === 4004) appendLog('⚠️ Session not found on server');
        else if (isDoneRef.current) appendLog('⏹ Monitoring stopped — validation complete');
        return;
      }
      appendLog('⚠️ Connection lost — reconnecting...');
      reconnectTimerRef.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => {
      setIsConnected(false);
    };
  }, [sessionId, enabled, containerPort, handleWsMessage, appendLog]);

  useEffect(() => {
    if (enabled && sessionId) {
      connectWs();
    }
    return () => {
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent auto-reconnect on unmount
        wsRef.current.close();
      }
    };
  }, [enabled, sessionId, connectWs]);

  if (!enabled) return null;

  const statusMeta = {
    idle: {cls: 'idle', label: 'Idle', icon: '⎈'},
    pending: {cls: 'pending', label: 'Pending...', icon: '⏳'},
    linting: {cls: 'linting', label: 'Linting...', icon: '🔍'},
    'running-dry-run': {cls: 'running', label: 'Dry-run...', icon: '🚀'},
    success: {cls: 'success', label: 'Passed', icon: '✅'},
    error: {cls: 'error', label: 'Issues found', icon: '❌'},
  };
  const sm = statusMeta[status] || statusMeta.idle;

  return (
    <div className="helm-preview-panel">
      {/* Header */}
      <div className="helm-header">
        <span className="helm-icon">⎈</span>
        <span className="helm-title">Helm Chart Validation</span>
        <span className={`helm-status-badge helm-status-${sm.cls}`}>
          {sm.icon} {sm.label}
        </span>
        <span className={`helm-ws-dot ${isConnected ? 'connected' : 'disconnected'}`} title={isConnected ? 'Live' : 'Disconnected'} />
        {onRunTest && (
          <button
            className="helm-run-btn"
            onClick={onRunTest}
            title="Re-run helm lint + dry-run"
            type="button"
          >
            ▶ Run Test
          </button>
        )}
      </div>

      {/* Summary bar */}
      <div className="helm-summary-bar">
        <span className={`helm-count ${errorCount > 0 ? 'has-error' : 'ok'}`}>
          ❌ {errorCount} error{errorCount !== 1 ? 's' : ''}
        </span>
        <span className={`helm-count ${warnCount > 0 ? 'has-warn' : 'ok'}`}>
          ⚠️ {warnCount} warning{warnCount !== 1 ? 's' : ''}
        </span>
        {lintResult && (
          <span className={`helm-phase ${lintResult.success ? 'ok' : 'has-error'}`}>
            Lint: {lintResult.success ? '✅' : '❌'}
          </span>
        )}
        {dryRunResult && (
          <span className={`helm-phase ${dryRunResult.success ? 'ok' : 'has-error'}`}>
            Dry-run: {dryRunResult.success ? '✅' : '❌'}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="helm-tabs">
        {['issues', 'templates', 'logs'].map(t => (
          <button
            key={t}
            className={`helm-tab ${activeTab === t ? 'active' : ''}`}
            onClick={() => setActiveTab(t)}
            type="button"
          >
            {t === 'issues' && `Issues (${allIssues.length})`}
            {t === 'templates' && 'Rendered Templates'}
            {t === 'logs' && 'Log'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="helm-content">
        {activeTab === 'issues' && (
          <div className="helm-issues">
            {allIssues.length === 0 ? (
              <div className="helm-empty">
                {status === 'idle' || status === 'pending' ? (
                  <span>⏳ Waiting for validation results...</span>
                ) : status === 'success' ? (
                  <span>✅ No issues found — chart looks good!</span>
                ) : (
                  <span>No issues captured yet</span>
                )}
              </div>
            ) : (
              allIssues.map((issue, idx) => (
                <div key={idx} className={`helm-issue helm-issue-${issue.severity}`}>
                  <div className="helm-issue-header">
                    <span className="helm-issue-sev">
                      {issue.severity === 'error' ? '❌' : issue.severity === 'warning' ? '⚠️' : 'ℹ️'}
                    </span>
                    <code className="helm-issue-file">{issue.file}{issue.line ? `:${issue.line}` : ''}</code>
                    <span className="helm-issue-src">[{issue.source}]</span>
                  </div>
                  <div className="helm-issue-msg">{issue.message}</div>
                  {issue.suggestion && (
                    <div className="helm-issue-suggestion">💡 {issue.suggestion}</div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="helm-templates-wrap">
            <div className="helm-templates-toolbar">
              <button
                className="helm-tpl-btn"
                onClick={handleCopyTemplates}
                title="Copy to clipboard"
                type="button"
              >
                {copied ? '✅ Copied' : '⎘ Copy'}
              </button>
              <button
                className="helm-tpl-btn"
                onClick={handleDownloadTemplates}
                title="Download as YAML"
                type="button"
              >
                ⬇ Download
              </button>
              <button
                className="helm-tpl-btn helm-tpl-btn--right"
                onClick={handleScrollTemplatesBottom}
                title="Scroll to bottom"
                type="button"
              >
                ↓ Bottom
              </button>
            </div>
            <pre className="helm-templates" ref={templatesRef}>
              {dryRunResult?.renderedTemplates
                ? dryRunResult.renderedTemplates
                : <span className="helm-muted">Waiting for helm template output...</span>}
            </pre>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="helm-templates-wrap">
            <div className="helm-templates-toolbar">
              <button
                className="helm-tpl-btn"
                onClick={handleCopyLogs}
                title="Copy logs to clipboard"
                type="button"
              >
                {logCopied ? '✅ Copied' : '⎘ Copy'}
              </button>
              <button
                className="helm-tpl-btn helm-tpl-btn--right"
                onClick={handleScrollLogsBottom}
                title="Scroll to bottom"
                type="button"
              >
                ↓ Bottom
              </button>
            </div>
            <div className="helm-logs">
              {logs.length === 0 ? (
                <div className="helm-empty">No log entries yet</div>
              ) : (
                logs.map((entry, idx) => (
                  <div key={idx} className="helm-log-entry">
                    <span className="helm-log-ts">{entry.ts}</span>
                    <span className="helm-log-msg">{entry.msg}</span>
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

HelmPreviewPanel.propTypes = {
  sessionId: PropTypes.string,
  projectId: PropTypes.string,
  serviceName: PropTypes.string,
  enabled: PropTypes.bool,
  containerPort: PropTypes.number,
  onRunTest: PropTypes.func,
};

export default HelmPreviewPanel;
